/**
 * Supabase Storage image transformations (render) URLs — resized + quality; Storage
 * serves WebP automatically for capable clients when transforms are used.
 * Docs: https://supabase.com/docs/guides/storage/serving/image-transformations
 *
 * Requires Image Transformations enabled on the Supabase project (Pro+).
 * To disable and use original URLs only, set in Netlify:
 *   REACT_APP_SUPABASE_IMAGE_TRANSFORM=false
 */

const DISABLED =
  typeof process !== 'undefined' &&
  process.env.REACT_APP_SUPABASE_IMAGE_TRANSFORM === 'false';

const OBJECT_PUBLIC = '/storage/v1/object/public/';

/**
 * Netlify Prerender (and similar) loads pages in headless Chrome and fetches gallery
 * `<picture>` sources — each unique `/render/image/` origin counts against Storage Image
 * Transformation quota. Skip transforms so automation uses `object/public` URLs only
 * (still rendered via `<img src>`); real browsers keep WebP `<source>` transforms.
 */
function skipTransformsForAutomatedClient() {
  if (typeof navigator === 'undefined') return false;
  if (navigator.webdriver === true) return true;
  const ua = navigator.userAgent || '';
  return /HeadlessChrome|Chrome-Lighthouse|Lighthouse|PageSpeedInsights/i.test(ua);
}

/**
 * @param {string} objectPublicUrl - Full URL from storage .../object/public/bucket/path
 * @param {{ width?: number, quality?: number, resize?: 'cover' | 'contain' | 'fill' }} [opts]
 * @returns {string|null} render/image URL or null if not a transformable Supabase object URL
 */
export function supabaseObjectUrlToRenderUrl(objectPublicUrl, opts = {}) {
  if (!objectPublicUrl || typeof objectPublicUrl !== 'string') return null;
  const i = objectPublicUrl.indexOf(OBJECT_PUBLIC);
  if (i === -1) return null;

  const origin = objectPublicUrl.slice(0, i);
  const after = objectPublicUrl.slice(i + OBJECT_PUBLIC.length);
  const pathOnly = after.split('?')[0];
  if (!pathOnly) return null;

  const width = opts.width ?? 1280;
  const quality = opts.quality ?? 78;
  const resize = opts.resize ?? 'contain';

  const renderPath = `${origin}/storage/v1/render/image/public/${pathOnly}`;
  // Fixed key order → identical query strings for the same opts (helps CDN/browser dedupe).
  const params = new URLSearchParams({
    width: String(width),
    quality: String(quality),
    resize,
  });
  return `${renderPath}?${params.toString()}`;
}

const DEFAULT_WIDTH_LG = 1280;
const DEFAULT_WIDTH_SM = 640;
const DEFAULT_QUALITY_LG = 78;
const DEFAULT_QUALITY_SM = 74;

/**
 * @param {string} originalUrl
 * @returns {{ webpSrcSet: string | null, fallback: string }}
 *   Responsive transform URLs for <picture>; fallback is always the original URL.
 */
export function getCourseGalleryImageSources(originalUrl) {
  if (!originalUrl || typeof originalUrl !== 'string') {
    return { webpSrcSet: null, fallback: originalUrl };
  }
  if (DISABLED) {
    return { webpSrcSet: null, fallback: originalUrl };
  }
  if (skipTransformsForAutomatedClient()) {
    return { webpSrcSet: null, fallback: originalUrl };
  }
  const wSm = supabaseObjectUrlToRenderUrl(originalUrl, {
    width: DEFAULT_WIDTH_SM,
    quality: DEFAULT_QUALITY_SM,
    resize: 'contain',
  });
  const wLg = supabaseObjectUrlToRenderUrl(originalUrl, {
    width: DEFAULT_WIDTH_LG,
    quality: DEFAULT_QUALITY_LG,
    resize: 'contain',
  });
  if (!wSm || !wLg) {
    return { webpSrcSet: null, fallback: originalUrl };
  }
  const webpSrcSet = `${wSm} ${DEFAULT_WIDTH_SM}w, ${wLg} ${DEFAULT_WIDTH_LG}w`;
  return { webpSrcSet, fallback: originalUrl };
}

